import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { useSearch } from "@tanstack/solid-router";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { Button, buttonVariants } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { useI18n } from "@/providers/i18n-provider";
import { api, createApiQuery, SERVER_BASE_URL } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { queryKeys, sharedApi } from "@time-tracker/shared";
import { showToast } from "@/components/ui/toast";
import { Link2, ShieldCheck } from "@fleet/icons";

type ProviderId = "google" | "apple";

/** `staleTime` for `auth.socialProviders` — MUST match `social-auth-buttons`. */
const PROVIDERS_STALE_TIME_MS = 5 * 60 * 1000;

const PROVIDER_LABELS: Record<ProviderId, string> = {
  google: "Google",
  apple: "Apple",
};

/**
 * "Connected accounts": lets users link/unlink Google and Apple from the
 * profile. Only shows providers ENABLED in the backend. It respects the
 * last-method safeguard (the backend rejects leaving the user without access).
 */
export function ConnectedAccountsSection() {
  const { t } = useI18n();
  // Reads ?linked=<provider> to show feedback after returning from the callback.
  const search = useSearch({ strict: false }) as () => { linked?: string };
  const [confirmUnlink, setConfirmUnlink] = createSignal<ProviderId | null>(null);
  const [busy, setBusy] = createSignal(false);

  // P23/CC-60 · P26. Both `queryFn`s used `data ?? {…}` with openapi-fetch,
  // which NEVER throws: a 503 resolved in SUCCESS with a FABRICATED object. The
  // `isError` branch below had existed since P23 but was DEAD code — unreachable —
  // and the card LIED: measured with `login-methods` returning 503, it said “Google · Not
  // connected” and “This is your only sign-in method” for an account whose state was
  // unknown. `isLastMethod`, the “do not leave yourself without access” safeguard,
  // also hangs off `hasPassword`. `createApiQuery` → `handleApiResponse`, which throws on
  // any non-2xx, like the rest of the repo.
  const providersQuery = createApiQuery(
    () => queryKeys.auth.socialProviders(),
    async () => sharedApi.socialProviders(api),
    () => ({ staleTime: PROVIDERS_STALE_TIME_MS, retry: false }),
  );

  const methodsQuery = createApiQuery(
    () => queryKeys.auth.loginMethods(),
    async () => sharedApi.loginMethods(api),
    () => ({ retry: false }),
  );

  /**
   * P23/CC-60 · P26 · the piece without which the other two are invisible.
   *
   * The section must be shown ALSO when loading fails. On error,
   * `enabledProviders()` is `[]`, so the OUTER `<Show>` hid the
   * ENTIRE card: no content, no error, no trace. The symptom was not “the error
   * message is missing” but “THE SECTION DOES NOT APPEAR”, which nobody looks for.
   */
  const hasLoadFailure = () => providersQuery.isError || methodsQuery.isError;

  const enabledProviders = createMemo<ProviderId[]>(() => {
    // P23 / CC-60 · `createMemo` is EAGER: reading `.data` occurs during
    // MOUNTING, before any `<Show>`, and `data` is the only field on the
    // solid-query proxy that suspends.
    if (!providersQuery.isSuccess) {
      return [];
    }
    const p = providersQuery.data;
    if (!p) {
      return [];
    }
    const list: ProviderId[] = [];
    if (p.google) {
      list.push("google");
    }
    if (p.apple) {
      list.push("apple");
    }
    return list;
  });

  // P23 / CC-60 · without the `isSuccess` guard, these `?? []` / `?? false` LIED:
  // when `login-methods` was down, the screen said “Not connected” for a Google account
  // that WAS connected, and “no password” for one that did — and
  // `hasPassword` hangs off `isLastMethod`, the “do not leave yourself without
  // access” safeguard. Also, `createMemo` is eager: the read suspended during mounting.
  const linkedProviders = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.providers ?? []) : [],
  );
  const hasPassword = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.has_password ?? false) : false,
  );

  const isLinked = (provider: ProviderId) => linkedProviders().includes(provider);

  // Total number of access methods (password + each linked social account).
  const totalMethods = createMemo(() => linkedProviders().length);

  // Is this the LAST method? (it cannot be unlinked without establishing another).
  const isLastMethod = (provider: ProviderId) =>
    isLinked(provider) && totalMethods() <= 1 && !hasPassword();

  const linkUrl = (provider: ProviderId) =>
    `${SERVER_BASE_URL.replace(/\/$/u, "")}/api/auth/link-social/${provider}`;

  const handleUnlink = async (provider: ProviderId) => {
    setBusy(true);
    try {
      const { error } = await sharedApi.unlinkAccount(api, provider);
      if (error) {
        const message = error.message ?? "";
        if (message.includes("only sign-in method")) {
          showToast(t(msg`Set a password before unlinking your only sign-in method.`), "error");
        } else {
          showToast(t(msg`Could not unlink the account. Please try again.`), "error");
        }
        return;
      }
      showToast(t(msg`Account unlinked.`), "success");
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    } finally {
      setBusy(false);
      setConfirmUnlink(null);
    }
  };

  // Feedback after returning from the linking flow (?linked=<provider>).
  let linkedFeedbackDone = false;
  createEffect(() => {
    const provider = search().linked;
    if (provider && !linkedFeedbackDone) {
      linkedFeedbackDone = true;
      showToast(t(msg`Account connected.`), "success");
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    }
  });

  return (
    <Show when={enabledProviders().length > 0 || hasLoadFailure()}>
      <Card>
        <CardHeader class="flex flex-row items-center gap-2 space-y-0">
          <ShieldCheck class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Connected accounts`)}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-3">
          {/* P23 / CC-60 · I3: se ENVUELVE el bloque (no se encadena
              `&& !isError` en el guard): en error `isPending` es `false`, así
              que el guard pintaría su esqueleto Y el error a la vez. */}
          <Show when={hasLoadFailure()}>
            <QueryErrorState
              testId="connected-accounts-error"
              compact
              onRetry={() => {
                // Retry ONLY the one(s) that failed.
                if (providersQuery.isError) {
                  void providersQuery.refetch();
                }
                if (methodsQuery.isError) {
                  void methodsQuery.refetch();
                }
              }}
            />
          </Show>
          <Show when={!hasLoadFailure()}>
            <Show
              when={!methodsQuery.isPending && !providersQuery.isPending}
              fallback={
                <div class="space-y-3">
                  <Skeleton class="h-14 w-full rounded-xl" />
                  <Skeleton class="h-14 w-full rounded-xl" />
                </div>
              }
            >
              <For each={enabledProviders()}>
                {(provider) => (
                  <div class="flex items-center gap-4 rounded-xl border border-border bg-muted/30 p-4">
                    <div class="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Link2 class="h-5 w-5" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-semibold">{PROVIDER_LABELS[provider]}</p>
                      <Show
                        when={isLinked(provider)}
                        fallback={
                          <p class="text-xs text-muted-foreground">{t(msg`Not connected`)}</p>
                        }
                      >
                        <Badge
                          variant="secondary"
                          class="mt-1 h-5 border-none bg-primary/20 px-1.5 text-[10px] font-bold uppercase tracking-wider text-primary"
                        >
                          {t(msg`Connected`)}
                        </Badge>
                      </Show>
                    </div>

                    <Show
                      when={isLinked(provider)}
                      fallback={
                        <a
                          href={linkUrl(provider)}
                          class={buttonVariants({ variant: "outline", size: "sm" })}
                          data-testid={`link-${provider}`}
                        >
                          {t(msg`Connect`)}
                        </a>
                      }
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy() || isLastMethod(provider)}
                        title={
                          isLastMethod(provider)
                            ? t(msg`Set a password before unlinking your only sign-in method.`)
                            : undefined
                        }
                        onClick={() => setConfirmUnlink(provider)}
                        class="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        {t(msg`Disconnect`)}
                      </Button>
                    </Show>
                  </div>
                )}
              </For>

              <Show when={!hasPassword() && totalMethods() <= 1}>
                <p class="text-xs text-muted-foreground">
                  {t(
                    msg`This is your only sign-in method. Set a password to be able to disconnect it.`,
                  )}
                </p>
              </Show>
            </Show>
          </Show>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmUnlink() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmUnlink(null);
          }
        }}
        title={t(msg`Disconnect account?`)}
        description={t(
          msg`You will no longer be able to sign in with this provider. You can reconnect it anytime.`,
        )}
        confirmLabel={t(msg`Disconnect`)}
        cancelLabel={t(msg`Cancel`)}
        onConfirm={() => {
          const provider = confirmUnlink();
          if (provider) {
            void handleUnlink(provider);
          }
        }}
      />
    </Show>
  );
}
